<template>
  <section class="sales-search tw-page">
    <PageHeader
      title="Sales Search"
      subtitle="Paste an RFP or job description below and we will look for matching based on the contacts in your Team."
    />

    <form class="sales-search__form" @submit.prevent="submitSearch">
      <b-field label="RFP or Job Posting">
        <b-input
          v-model="rfpText"
          type="textarea"
          rows="10"
          placeholder="Paste the full job posting or RFP here"
          required
        />
      </b-field>

      <div class="sales-search__actions">
        <b-button
          type="is-primary"
          native-type="submit"
          :loading="isSubmitting"
          :disabled="isSubmitDisabled"
        >
          Search
        </b-button>
      </div>
    </form>

    <b-message
      v-if="errorMessage"
      type="is-danger"
      aria-close-label="Close notification"
      :closable="true"
      @close="errorMessage = null"
    >
      {{ errorMessage }}
    </b-message>

    <section v-if="submissionResult" class="sales-search__results">
      <h2>Results</h2>

      <template v-if="hasMatchData">
        <div v-if="matchesCurrent.length" class="match-section">
          <h3>Current Fit Matches</h3>
          <SalesMatchCard
            v-for="match in matchesCurrent"
            :key="match.user?._id"
            :match="match"
          />
        </div>

        <div v-if="matchesAspirational.length" class="match-section">
          <h3>Aspirational Matches</h3>
          <SalesMatchCard
            v-for="match in matchesAspirational"
            :key="`${match.user?._id}-aspirational`"
            :match="match"
            variant="aspirational"
          />
        </div>
      </template>

      <p v-else class="no-results">No matches returned for this search yet. Try refining the posting and resubmitting.</p>

      <details class="raw-response">
        <summary>View raw response</summary>
        <pre>{{ formattedResult }}</pre>
      </details>
    </section>
  </section>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader.vue'
import SalesMatchCard from '@/components/sales/SalesMatchCard.vue'


export default {
  name: 'SalesModule',

  components: {
    PageHeader,
    SalesMatchCard,
  },

  data() {
    return {
      rfpText: '',
      isSubmitting: false,
      submissionResult: null,
      errorMessage: null,

      config: {
        headers: { 'Authorization': this.$cookies.get('teamwrkr-token') }
      }
    }
  },

  beforeCreate() {
    if (!this.$cookies.isKey('teamwrkr-token')) {
      this.$router.push('/welcome')
    }
  },

  computed: {
    isSubmitDisabled() {
      return this.isSubmitting || !this.rfpText.trim()
    },
    matchesCurrent() {
      if (!this.submissionResult || !Array.isArray(this.submissionResult.matchesCurrent)) {
        return []
      }
      return this.submissionResult.matchesCurrent
    },
    matchesAspirational() {
      if (!this.submissionResult || !Array.isArray(this.submissionResult.matchesAspirational)) {
        return []
      }
      return this.submissionResult.matchesAspirational
    },
    hasMatchData() {
      return this.matchesCurrent.length > 0 || this.matchesAspirational.length > 0
    },
    formattedResult() {
      if (!this.submissionResult) {
        return ''
      }
      if (typeof this.submissionResult === 'string') {
        return this.submissionResult
      }
      try {
        return JSON.stringify(this.submissionResult, null, 2)
      } catch (error) {
        return this.submissionResult
      }
    }
  },

  created() {

  },


  methods: {
    async submitSearch() {
      if (this.isSubmitDisabled) {
        return
      }

      this.isSubmitting = true
      this.errorMessage = null
      this.submissionResult = null

      try {
        const response = await this.axios.post(
          `${process.env.VUE_APP_API_URL}/users/search`,
          { text: this.rfpText },
          this.config
        )
        this.submissionResult = response.data
        console.log('REsults, ', this.submissionResult)
      } catch (error) {
        console.error('Error submitting sales search:', error)
        this.errorMessage = error.response?.data?.message || 'Unable to run the search right now. Please try again.'
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>


<style lang="scss" scoped>
.sales-search {
  max-width: 960px;
  margin: 0 auto;
  padding: $space-6 $space-4 $space-7;

  &__actions {
    display: flex;
    justify-content: flex-end;
  }

  &__results {
    margin-top: $space-6;
    padding: $space-5;
    background: $color-surface-soft;
    border-radius: $radius-md;

    h2 {
      margin-bottom: $space-4;
    }

    pre {
      background: $color-surface;
      border-radius: $radius-sm;
      padding: $space-4;
      overflow: auto;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }

  .match-section {
    margin-top: $space-5;

    h3 {
      margin-bottom: $space-4;
    }
  }

  .no-results {
    margin-top: $space-5;
    font-style: italic;
    color: $color-text-subtle;
  }

  .raw-response {
    margin-top: $space-4;

    summary {
      cursor: pointer;
      font-weight: 600;
      margin-bottom: $space-2;
    }
  }
}

</style>
